import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { pueblosPuertoRico, categorias } from '../../mocks/pueblos';
import { useProducts } from '../../hooks/useProducts';

const slides = [
  {
    id: 1,
    image: 'https://public.readdy.ai/ai/img_res/fd99cd63e72a8a4ebfe2d6f1a727ff53.jpg',
    tag: 'Moda y Belleza',
    tagIcon: 'ri-t-shirt-line',
    titulo: '¡Las mejores marcas al mejor precio!',
    subtitulo: 'Tenis, ropa y accesorios de marca directo en tu pueblo.',
    boton: 'Ver Tenis',
    link: '/catalogo?categoria=moda',
    color: 'from-rose-900/70 via-rose-800/40 to-transparent',
  },
  {
    id: 2,
    image: 'https://readdy.ai/api/search-image?query=modern%20elegant%20living%20room%20interior%20with%20stylish%20sofa%20couch%20set%20contemporary%20furniture%20design%20warm%20lighting%20luxury%20home%20decor%20neutral%20tones%20beige%20gray%20professional%20interior%20photography%20wide%20angle%20spacious%20bright%20apartment&width=1920&height=900&seq=slide-hogar-2&orientation=landscape',
    tag: 'Hogar y Enseres',
    tagIcon: 'ri-home-4-line',
    titulo: 'Renueva tu hogar con envío por Island Wide',
    subtitulo: 'Neveras, muebles y enseres con entrega garantizada a toda la isla.',
    boton: 'Ver Enseres',
    link: '/catalogo?categoria=hogar',
    color: 'from-emerald-900/70 via-emerald-800/40 to-transparent',
  },
  {
    id: 3,
    image: 'https://readdy.ai/api/search-image?query=latest%20iPhone%20smartphone%20and%20MacBook%20laptop%20placed%20on%20sleek%20dark%20desk%20with%20soft%20ambient%20lighting%20modern%20technology%20gadgets%20premium%20electronics%20product%20photography%20minimalist%20setup%20clean%20background%20high%20resolution%20detail&width=1920&height=900&seq=slide-tech-3&orientation=landscape',
    tag: 'Tecnología y Celulares',
    tagIcon: 'ri-smartphone-line',
    titulo: 'Lo último en tecnología y celulares usados',
    subtitulo: 'iPhones, laptops y gadgets verificados con garantía del vendedor.',
    boton: 'Ver Tech',
    link: '/catalogo?categoria=tecnologia',
    color: 'from-slate-900/70 via-slate-800/40 to-transparent',
  },
  {
    id: 4,
    image: 'https://readdy.ai/api/search-image?query=sleek%20modern%20sports%20car%20luxury%20vehicle%20parked%20on%20urban%20street%20Puerto%20Rico%20tropical%20setting%20palm%20trees%20background%20golden%20hour%20sunset%20light%20dramatic%20automotive%20photography%20wide%20angle%20professional%20car%20dealership&width=1920&height=900&seq=slide-autos-4&orientation=landscape',
    tag: 'Autos y Accesorios',
    tagIcon: 'ri-car-line',
    titulo: '¡Encuentra tu próximo carro en la isla!',
    subtitulo: 'Vehículos verificados. Coordina directamente con el vendedor o dealer.',
    boton: 'Ver Autos',
    link: '/catalogo?categoria=autos',
    color: 'from-amber-900/70 via-amber-800/40 to-transparent',
  },
];

const HeroSlider = ({
  searchQuery, setSearchQuery,
  selectedPueblo, setSelectedPueblo,
  selectedCategoria, setSelectedCategoria,
  handleSearch,
}: {
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  selectedPueblo: string;
  setSelectedPueblo: (v: string) => void;
  selectedCategoria: string;
  setSelectedCategoria: (v: string) => void;
  handleSearch: (e: React.FormEvent) => void;
}) => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 400);
  }, [animating]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center pt-20 overflow-hidden">
      {/* Slides background */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
          style={{
            backgroundImage: `url('${s.image}')`,
            opacity: i === current ? 1 : 0,
            zIndex: i === current ? 1 : 0,
          }}
        />
      ))}

      {/* Dark overlay always on top of image */}
      <div className="absolute inset-0 bg-black/50 z-10" />

      {/* Gradient overlay per slide */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${slide.color} z-10 transition-all duration-700`}
      />

      {/* Content */}
      <div className="relative z-20 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

        {/* Main title always visible */}
        <p className="text-sm font-semibold text-white/80 uppercase tracking-widest mb-3">
          IslaVende · Puerto Rico 🇵🇷
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight drop-shadow-lg">
          ¡Compra y Vende en Puerto Rico<br />con Seguridad Total!
        </h1>

        {/* Slide tag badge */}
        <div
          className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4 transition-all duration-500"
          style={{ opacity: animating ? 0 : 1, transform: animating ? 'translateY(8px)' : 'translateY(0)' }}
        >
          <i className={`${slide.tagIcon} text-base`}></i>
          {slide.tag}
        </div>

        {/* Slide headline */}
        <div
          className="transition-all duration-500 mb-2"
          style={{ opacity: animating ? 0 : 1, transform: animating ? 'translateY(12px)' : 'translateY(0)' }}
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-md">
            {slide.titulo}
          </h2>
          <p className="text-white/85 text-base mt-2 mb-5">
            {slide.subtitulo}
          </p>
          <Link
            to={slide.link}
            className="inline-flex items-center gap-2 bg-white text-gray-900 font-bold px-6 py-2.5 rounded-full hover:bg-gray-100 transition-colors shadow-lg text-sm whitespace-nowrap cursor-pointer mb-8"
          >
            <i className="ri-arrow-right-circle-line text-lg"></i>
            {slide.boton}
          </Link>
        </div>

        {/* Search box */}
        <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-7">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="¿Qué estás buscando? iPhone, sofá, carro..."
                className="w-full pl-14 pr-4 py-4 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cobalt-500 transition-colors"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="relative">
                <i className="ri-map-pin-line absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400"></i>
                <select
                  value={selectedPueblo}
                  onChange={(e) => setSelectedPueblo(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cobalt-500 transition-colors appearance-none bg-white cursor-pointer"
                >
                  <option value="">Todos los pueblos</option>
                  {pueblosPuertoRico.map((pueblo) => (
                    <option key={pueblo} value={pueblo}>{pueblo}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none"></i>
              </div>
              <div className="relative">
                <i className="ri-apps-line absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400"></i>
                <select
                  value={selectedCategoria}
                  onChange={(e) => setSelectedCategoria(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cobalt-500 transition-colors appearance-none bg-white cursor-pointer"
                >
                  <option value="">Todas las categorías</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none"></i>
              </div>
            </div>
            <button
              type="submit"
              className="w-full py-4 bg-cobalt-500 text-white text-lg font-semibold rounded-xl hover:bg-cobalt-600 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-line mr-2"></i>
              Buscar Ahora
            </button>
          </form>
        </div>
      </div>

      {/* Prev / Next arrows */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-11 h-11 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm border border-white/30 text-white rounded-full transition-all cursor-pointer"
        aria-label="Anterior"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-11 h-11 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm border border-white/30 text-white rounded-full transition-all cursor-pointer"
        aria-label="Siguiente"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current
                ? 'w-8 h-2.5 bg-white'
                : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Ir al slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPueblo, setSelectedPueblo] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const { getProducts, loading } = useProducts();
  const [productosDestacados, setProductosDestacados] = useState<any[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      const { data } = await getProducts();
      if (data) {
        setProductosDestacados(data.slice(0, 8));
      }
    };
    fetchProducts();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.append('q', searchQuery);
    if (selectedPueblo) params.append('pueblo', selectedPueblo);
    if (selectedCategoria) params.append('categoria', selectedCategoria);
    navigate(`/catalogo?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <HeroSlider
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedPueblo={selectedPueblo}
        setSelectedPueblo={setSelectedPueblo}
        selectedCategoria={selectedCategoria}
        setSelectedCategoria={setSelectedCategoria}
        handleSearch={handleSearch}
      />

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            Explora por Categorías
          </h2>
          <p className="text-center text-gray-600 mb-12 text-base">
            Encuentra exactamente lo que necesitas en nuestras categorías populares
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categorias.map((cat) => (
              <Link
                key={cat.id}
                to={`/catalogo?categoria=${cat.id}`}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-cobalt-500 cursor-pointer"
              >
                <div className="w-16 h-16 mx-auto mb-3 bg-cobalt-50 rounded-full flex items-center justify-center">
                  <i className={`${cat.icon} text-3xl text-cobalt-500`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-sm">{cat.nombre}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                Productos Destacados
              </h2>
              <p className="text-gray-600 text-base">Las mejores ofertas del momento</p>
            </div>
            <Link
              to="/catalogo"
              className="hidden sm:flex items-center text-cobalt-500 font-semibold hover:text-cobalt-600 transition-colors text-sm cursor-pointer whitespace-nowrap"
            >
              Ver todos
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-cobalt-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : productosDestacados.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-12 text-center">
              <i className="ri-shopping-bag-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Aún no hay productos publicados
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                Sé el primero en publicar tus artículos
              </p>
              <Link
                to="/registro"
                className="inline-block px-6 py-3 bg-cobalt-500 text-white font-semibold rounded-lg hover:bg-cobalt-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Comenzar a Vender
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {productosDestacados.map((producto) => (
                <Link
                  key={producto.id}
                  to={`/producto/${producto.id}`}
                  className="bg-white rounded-xl overflow-hidden border-2 border-gray-100 hover:border-cobalt-500 hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer"
                >
                  <div className="relative w-full h-56">
                    <img
                      src={producto.fotos?.[0] || 'https://readdy.ai/api/search-image?query=product%20placeholder%20image%20simple%20clean%20white%20background&width=400&height=400&seq=placeholder1&orientation=squarish'}
                      alt={producto.titulo}
                      className="w-full h-full object-cover object-top"
                    />
                    <span className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-700">
                      {producto.estado === 'nuevo' ? 'Nuevo' : 'Usado'}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm line-clamp-2">
                      {producto.titulo}
                    </h3>
                    <div className="flex items-center text-xs text-gray-500 mb-3">
                      <i className="ri-map-pin-line mr-1"></i>
                      {producto.pueblo}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-cobalt-500">
                        ${producto.precio}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-8 sm:hidden">
            <Link
              to="/catalogo"
              className="inline-flex items-center text-cobalt-500 font-semibold hover:text-cobalt-600 transition-colors text-sm cursor-pointer whitespace-nowrap"
            >
              Ver todos los productos
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-cobalt-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-4xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Compra Protegida</h3>
              <p className="text-cobalt-100 text-sm">
                Tu dinero está seguro hasta que recibas tu producto en perfectas condiciones
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-truck-line text-4xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Envío Island Wide</h3>
              <p className="text-cobalt-100 text-sm">
                Entrega garantizada a toda la isla con opciones de envío regular y carga pesada
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-user-forbid-line text-4xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Sin Encuentros</h3>
              <p className="text-cobalt-100 text-sm">
                Olvídate de reuniones incómodas. Todo se maneja de forma segura y profesional
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                ¿Tienes algo que vender?
              </h2>
              <p className="text-gray-600 mb-6 text-base leading-relaxed">
                Publica tus artículos en minutos y llega a miles de compradores en toda la isla. Sin complicaciones, sin riesgos.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-2xl text-cobalt-500 mr-3 mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Publicación gratuita</strong>
                    <p className="text-gray-600 text-sm">Sin costos ocultos ni comisiones sorpresa</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-2xl text-cobalt-500 mr-3 mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Pagos seguros</strong>
                    <p className="text-gray-600 text-sm">Recibe tu dinero con ATH Móvil o transferencia</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-2xl text-cobalt-500 mr-3 mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Gestión de envíos</strong>
                    <p className="text-gray-600 text-sm">Nosotros coordinamos todo con Island Wide</p>
                  </div>
                </li>
              </ul>
              <Link
                to="/registro"
                className="inline-block px-8 py-4 bg-cobalt-500 text-white font-semibold rounded-xl hover:bg-cobalt-600 transition-colors shadow-lg text-base whitespace-nowrap cursor-pointer"
              >
                Comenzar a Vender
              </Link>
            </div>

            <div className="relative w-full h-96 lg:h-full min-h-96">
              <img
                src="https://readdy.ai/api/search-image?query=happy%20smiling%20Puerto%20Rican%20person%20holding%20smartphone%20and%20cardboard%20package%20celebrating%20successful%20online%20sale%20bright%20cheerful%20expression%20modern%20casual%20clothing%20clean%20white%20background%20professional%20lifestyle%20photography&width=800&height=600&seq=seller1&orientation=landscape"
                alt="Vender en IslaVende"
                className="w-full h-full object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
